import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import apiClient from "@/app/api/client";
import { usePermission } from "@/shared/hooks/usePermission";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { employeesService } from "../services/employees.service";
import { fetchEmployeeZones } from "../services/employeeZones.api";

/**
 * Xodim profilining hooklari.
 *
 * ⚠️ **Nega alohida fayl, `useEmployees.ts` emas.** Profil sahifasi sakkiz
 * yangi hook keltiradi va `useEmployees.ts` allaqachon 286 qator — ro'yxat
 * hamda forma hooklari bilan aralashib, ikkala tomonni ham o'qish
 * qiyinlashardi. Chegara mavzu bo'yicha: bu yerda FAQAT `/employees/$id`
 * sahifasining so'rovlari turadi.
 */

/**
 * Vaqtinchalik parolni qayta berish.
 *
 * ⚠️ **Kesh invalidatsiyasi ATAYLAB yo'q.** Parol hech qanday so'rov
 * javobida qaytmaydi va hech bir keshlangan qator o'zgarmaydi — mavjud
 * kalitlarni bekor qilish faqat keraksiz refetch keltirardi.
 *
 * ⚠️ Natija — parolning O'ZI: chaqiruvchi uni `TemporaryPasswordModal`
 * ga uzatadi. U hech qayerda saqlanmaydi, ya'ni modal yopilgach yagona
 * yo'l — parolni yana tiklash.
 */
export function useResetEmployeePassword() {
	const { message } = App.useApp();
	const { t } = useTranslation("employees");

	return useMutation({
		mutationFn: (userId: string) => employeesService.resetPassword(userId),
		onError: (error) => {
			message.error(getErrorMessage(error, t("messages.resetPasswordError")));
		},
	});
}

/**
 * Xodimning hudud biriktirishlari.
 *
 * ⚠️⚠️ **Darvoza `zones:read`, backend darvozasi esa `employees:read`**
 * (`employee-zones.routes.ts`). Farq ATAYLAB va u fail-closed tomonga
 * ketadi: hududning NOMI hudud resursining ma'lumoti, ya'ni `zones:read`
 * siz foydalanuvchi ustunni umuman ko'rmaydi. Ikki darvozani
 * moslashtirish P ning kontrakt hujjatida hal qilinadi; hozircha
 * ikkalasi ham cheklovchi tomonda.
 */
export function useEmployeeZoneAssignments(employeeId: string | undefined) {
	const { can } = usePermission();

	return useQuery({
		queryKey: ["employees", "zones", employeeId],
		queryFn: () => fetchEmployeeZones(employeeId as string),
		enabled: Boolean(employeeId) && can("zones", "read"),
		staleTime: 60_000,
	});
}

interface EmployeeDeviceRow {
	id: string;
	model: string | null;
	os: string | null;
	osVersion: string | null;
	isActive: boolean;
}

/**
 * Xodimning AMALDAGI qurilmasi — fakt ustuni uchun bittasi yetarli.
 *
 * ⚠️ `isActive: "true"` ATAYLAB uzatiladi. Backend'da bu parametrga
 * STANDART BERILMAGAN (`devices.schemas.ts:39-45`) — `undefined` faol va
 * bekor qilingan IKKALASINI qaytaradi, ya'ni fakt qatorida allaqachon
 * bog'lanmagan eski telefon ko'rinib qolishi mumkin edi.
 *
 * ⚠️ `z.coerce.boolean()` emas, SATR: backend `"true"`/`"false"` satrini
 * kutadi.
 */
export function useEmployeeDevice(employeeId: string | undefined) {
	const { can } = usePermission();

	return useQuery({
		queryKey: ["employees", "device", employeeId],
		queryFn: async () => {
			const response = await apiClient.get<{
				success: true;
				data: { items: EmployeeDeviceRow[] };
			}>("/devices", {
				params: { employeeId, isActive: "true", page: 1, limit: 1 },
			});

			return response.data.data.items;
		},
		enabled: Boolean(employeeId) && can("devices", "read"),
		staleTime: 60_000,
	});
}

interface RoleAssignmentRow {
	id: string;
	roleId: string;
	role?: { id: string; name: string };
}

/**
 * Mobil ilova roli — `GET /role-assignments?subjectId=`.
 *
 * ⚠️⚠️ `subjectId` — **`users.id`**, `employees.id` EMAS
 * (`role-assignments.schemas.ts:83`). Xodim id si yuborilsa filtr
 * JIMGINA nol qator qaytaradi va ustun har xodimda «—» bo'lib
 * qolaverardi — hech qanday xato ko'rinmasdi.
 *
 * ⚠️ Rol NOMI `role` ichida va u OPTIONAL (`RoleSchema.optional()`);
 * bo'lmasa `roleId` ga tushiladi.
 *
 * ⚠️ Urug'langan `Rahbar` rolida `role-assignments:read` YO'Q — kanvasdagi
 * personaj bu ustunni ko'rmaydi. Bu ONGLI: rolga ruxsat qo'shish
 * imtiyozni kengaytirardi (CLAUDE.md, «Kirish ko'lami» 4-qoidasi).
 */
export function useEmployeeAppRole(userId: string | undefined) {
	const { can } = usePermission();

	return useQuery({
		queryKey: ["employees", "app-role", userId],
		queryFn: async () => {
			const response = await apiClient.get<{
				success: true;
				data: { items: RoleAssignmentRow[] };
			}>("/role-assignments", {
				params: { subjectId: userId, page: 1, limit: 20 },
			});

			return response.data.data.items;
		},
		enabled: Boolean(userId) && can("role-assignments", "read"),
		staleTime: 60_000,
	});
}

/**
 * Xavf bayrog'ini «ko'rib chiqilgan» deb belgilash.
 *
 * ⚠️⚠️ **Uchala kesh kalitini ham invalidatsiya qiladi.** Mavjud
 * `useAcknowledgeRisk` (`modules/dashboard/hooks/useDashboard.ts`) faqat
 * `["risky-employees"]` va `["dashboard"]` ni bekor qiladi,
 * `["employees","detail",id]` ni EMAS — profildan chaqirilganda
 * «Ko'rilmagan» tegi bosishdan keyin ham turaverardi va foydalanuvchi
 * tugmani qayta bosardi. Hech qanday xato ko'rinmasdi.
 *
 * ⚠️ Mavjud IKKI kalit SAQLANADI: profildan belgilangan xodim dashboard
 * ro'yxatidan ham chiqishi kerak.
 *
 * ⚠️ **ONGLI QARZ:** bu hook `useAcknowledgeRisk` bilan ikki nusxa.
 * Ularni birlashtirish `modules/dashboard` ga tegishni talab qiladi va
 * u 3-bosqichda boshqa blokning hududi. Merge'dan keyin alohida ish.
 *
 * ⚠️ Bayroqning O'ZI o'zgarmaydi — u hisoblanadi (kontrakt §7.5), ya'ni
 * qayta hisoblashdan keyin bayroq QAYTISHI mumkin. Shu sababdan
 * «bayroq bor, lekin ko'rilgan» holati mavjud va unga alohida matn
 * kerak.
 */
export function useAcknowledgeEmployeeRisk(employeeId: string) {
	const { message } = App.useApp();
	const { t } = useTranslation("employees");
	const queryClient = useQueryClient();

	return useMutation({
		// ⚠️ `reason` `min(3)` — sababsiz so'rov 422 qaytaradi.
		mutationFn: (reason: string) =>
			apiClient.post(`/employees/${employeeId}/risk/acknowledge`, { reason }),
		onSuccess: () => {
			message.success(t("messages.riskAcknowledged"));
			queryClient.invalidateQueries({ queryKey: ["employees", "detail", employeeId] });
			queryClient.invalidateQueries({ queryKey: ["risky-employees"] });
			queryClient.invalidateQueries({ queryKey: ["dashboard"] });
		},
		onError: (error) => {
			message.error(getErrorMessage(error, t("messages.riskAcknowledgeError")));
		},
	});
}

interface RecentAbsence {
	id: string;
	type: string;
	dateFrom: string;
	dateTo: string;
	status: string;
}

/**
 * Yon ustundagi «Oxirgi arizalar» kartasi uchun uchta ariza.
 *
 * ⚠️⚠️ **Tartib SERVERDA va klientda QAYTA tartiblanmaydi.**
 * `absences.repository.ts` `.orderBy(desc(dateFrom), desc(createdAt))`
 * qiladi, ya'ni birinchi sahifaning birinchi uch qatori HAQIQATAN eng
 * oxirgilari. Mijozda ikkinchi tartib qo'yilsa ikki joyda ikki tartib
 * bo'lardi va ular server tomoni o'zgarganda jimgina ajralardi.
 *
 * ⚠️ Darvoza `absences:read` — urug'langan `Rahbar` da BOR. Ruxsat
 * bo'lmasa so'rov UMUMAN ketmaydi, aks holda har profil ochilishida 403
 * loglanardi.
 */
export function useRecentAbsences(employeeId: string | undefined) {
	const { can } = usePermission();

	return useQuery({
		queryKey: ["employees", "recent-absences", employeeId],
		queryFn: async () => {
			const response = await apiClient.get<{
				success: true;
				data: { items: RecentAbsence[] };
			}>("/absences", {
				params: { employeeId, page: 1, limit: 3 },
			});

			return response.data.data.items;
		},
		enabled: Boolean(employeeId) && can("absences", "read"),
		staleTime: 60_000,
	});
}
